import { useEffect, useMemo, useState } from 'react'

import { Button as MantineButton, Drawer } from '@mantine/core'
import { useDisclosure, useReducedMotion } from '@mantine/hooks'

import { Link, routes, useLocation } from '@redwoodjs/router'
import { MetaTags } from '@redwoodjs/web'
import { useQuery } from '@redwoodjs/web'

import { GET_JOB_LISTING_BY_TRACKING_LINK } from 'src/graphql/trackinglinks.graphql'
import { unformatPhoneNumber } from 'src/lib/phone.utils'
import { cn } from 'src/utils'

import Header from '../DriverApplicationPage/components/Header'

import FormSubmitScreen from './FormSubmitScreen'
import {
  formatLocation,
  formatPay,
  hasJobInfo,
  ShortFormJobListing,
} from './jobInfo.utils'
import JobInfoPanel, { JobHeaderChips } from './JobInfoPanel'
import ShortFormCard from './ShortFormCard'
import {
  PublicTemplateQuestion,
  sortPublicQuestions,
} from './shortFormQuestions.utils'
import { useIsDesktop } from './useIsDesktop'
import { useShortFormSubmit } from './useShortFormSubmit'

// Slide without the default opacity fade: a bottom sheet is a physical
// surface, it moves in from the edge rather than dissolving.
const APPLY_SHEET_TRANSITION = {
  in: { transform: 'translateY(0)' },
  out: { transform: 'translateY(100%)' },
  common: { transformOrigin: 'bottom' },
  transitionProperty: 'transform',
}

/**
 * Back to the carrier's board. Shown to everyone, not only board arrivals —
 * for a driver who came from the mobile board it's the only way back, and an
 * Indeed arrival gets a path to the carrier's other openings.
 */
const AllPositionsLink = ({
  companyId,
  boardSearch,
  phone,
  className,
}: {
  companyId?: string | null
  boardSearch?: string
  phone?: string
  className?: string
}) => {
  if (!companyId) {
    return null
  }
  // Restore the board's search (?q=) so Back lands on the same filtered list,
  // and carry the /c phone hint so the driver still skips a second login.
  const params = new URLSearchParams()
  if (boardSearch) params.set('q', boardSearch)
  if (phone) params.set('phone', phone)
  const queryString = params.toString()
  const to = `${routes.publicShortForm({ companyId })}${
    queryString ? `?${queryString}` : ''
  }`
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center gap-1.5 self-start text-[13px] font-semibold text-doubleNickel-gray-600 hover:text-doubleNickel-brand-600',
        className
      )}
    >
      <svg
        className="h-[15px] w-[15px] flex-none text-doubleNickel-gray-500"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
      All positions
    </Link>
  )
}

const ShortFormPage = ({ trackingLink }) => {
  const [resumeFile, setResumeFile] = useState<File[]>([])
  const [applySheetOpened, { open: openApplySheet, close: closeApplySheet }] =
    useDisclosure(false)
  const isDesktop = useIsDesktop()
  const reduceMotion = useReducedMotion()
  const { search } = useLocation()

  // Set by the public job board when it hands off on mobile, so "All
  // positions" can restore the list exactly as the driver left it.
  const boardSearch = new URLSearchParams(search).get('boardSearch') ?? ''
  // The /c phone hint threaded through the hand-off — kept so "All positions"
  // doesn't drop it on the way back to the board.
  const phoneHint = new URLSearchParams(search).get('phone') ?? ''

  const { data: jobListingData } = useQuery(GET_JOB_LISTING_BY_TRACKING_LINK, {
    variables: {
      link: trackingLink,
    },
    onError: () => {
      window.location.href = '/404'
    },
  })

  const jobListing: ShortFormJobListing | null =
    jobListingData?.jobListingByTrackingLink ?? null

  // Short-form template questions render inline after the fixed fields, in
  // the template's drag order
  const publicQuestions: PublicTemplateQuestion[] = useMemo(
    () => sortPublicQuestions(jobListing?.publicQuestions ?? []),
    [jobListing?.publicQuestions]
  )

  // Validation, the createBasicApplicant mutation and the driver/office
  // post-submit branch are shared with the public job board's copy of this
  // form — see useShortFormSubmit.
  const {
    form,
    isOfficeJob,
    loading: loadingSubmitApplicant,
    handleSubmission,
    isSubmitted,
    applicationId,
    turnstile,
  } = useShortFormSubmit({
    jobListing,
    trackingLink,
    resumeFile,
    publicQuestions,
    // The bottom sheet is this page's own surface — close it before the
    // office confirmation screen replaces the layout underneath it.
    onSubmitted: closeApplySheet,
  })

  useEffect(() => {
    if (jobListingData?.jobListingByTrackingLink === null) {
      window.location.href = '/404'
    }
  }, [jobListingData])

  // The bottom sheet only exists on mobile — close it when the viewport
  // crosses to desktop so it isn't left open if the user resizes back.
  useEffect(() => {
    if (isDesktop) {
      closeApplySheet()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop])

  if (!jobListing) {
    return null
  }

  const showJobInfo = hasJobInfo(jobListing)
  const compactPay = formatPay(jobListing, { compact: true })
  const location = formatLocation(jobListing.address)
  const applyBarSummary = [location, jobListing.subtitle]
    .filter(Boolean)
    .join(' · ')

  const formCard = (
    <ShortFormCard
      form={form}
      isOfficeJob={isOfficeJob}
      resumeFile={resumeFile}
      setResumeFile={setResumeFile}
      companyId={jobListing.company?.companyId}
      companyName={jobListing.company?.companyName}
      publicQuestions={publicQuestions}
      loading={loadingSubmitApplicant}
      onSubmit={handleSubmission}
      turnstile={turnstile}
      fullWidthSubmit={!isDesktop}
    />
  )

  return (
    <div className="relative flex h-[100dvh] flex-col overflow-hidden bg-doubleNickel-gray-50">
      <MetaTags title="ShortForm" description="ShortForm page" />
      <Header companyId={jobListing.company?.companyId} />

      {!isSubmitted && !showJobInfo && (
        // Fallback — no job description data. The interest form is the page:
        // a single centered card with the title and chips above the form
        // (designs 1d desktop / 3c mobile)
        <div className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[620px] p-3.5 sm:px-8 sm:py-7">
            <AllPositionsLink
              companyId={jobListing.company?.companyId}
              boardSearch={boardSearch}
              phone={phoneHint}
              className="mb-2.5 sm:mb-3.5"
            />
            <div className="rounded-xl border border-doubleNickel-gray-200 bg-white p-4 sm:p-8">
              <h1 className="text-lg font-extrabold leading-snug text-doubleNickel-gray-900 sm:text-2xl">
                {jobListing.title}
              </h1>
              {(location || jobListing.company?.companyName) && (
                <div className="mt-3 border-b border-doubleNickel-gray-100 pb-4 sm:pb-5">
                  <JobHeaderChips
                    location={location}
                    companyName={jobListing.company?.companyName}
                  />
                </div>
              )}
              <div className="mt-4 sm:mt-5">
                <ShortFormCard
                  form={form}
                  isOfficeJob={isOfficeJob}
                  resumeFile={resumeFile}
                  setResumeFile={setResumeFile}
                  companyId={jobListing.company?.companyId}
                  companyName={jobListing.company?.companyName}
                  publicQuestions={publicQuestions}
                  loading={loadingSubmitApplicant}
                  onSubmit={handleSubmission}
                  turnstile={turnstile}
                  fullWidthSubmit
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {!isSubmitted && showJobInfo && isDesktop && (
        // Desktop — job info and sticky application form side by side.
        // The form card keeps its own persistent Apply bar pinned at its
        // bottom; the fields scroll inside the card when it runs tall.
        <div className="flex-1 overflow-auto">
          <main className="mx-auto w-full max-w-[1320px] px-6 py-8">
            <AllPositionsLink
              companyId={jobListing.company?.companyId}
              boardSearch={boardSearch}
              phone={phoneHint}
              className="mb-3.5"
            />
            <div className="grid grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)] items-start gap-8">
              <JobInfoPanel jobListing={jobListing} />
              {/* top-8 matches the container's py-8 so the card pins exactly
                where it rests instead of lurching up on the first scroll */}
              <div className="sticky top-8">
                <div className="relative flex max-h-[calc(100dvh-9rem)] flex-col overflow-hidden rounded-xl border border-doubleNickel-gray-200 bg-white">
                  <div
                    className={`flex-1 overflow-y-auto p-8 ${
                      compactPay ? '' : 'pb-24'
                    }`}
                  >
                    <ShortFormCard
                      form={form}
                      isOfficeJob={isOfficeJob}
                      resumeFile={resumeFile}
                      setResumeFile={setResumeFile}
                      companyId={jobListing.company?.companyId}
                      companyName={jobListing.company?.companyName}
                      publicQuestions={publicQuestions}
                      loading={loadingSubmitApplicant}
                      onSubmit={handleSubmission}
                      turnstile={turnstile}
                      showSubmit={false}
                    />
                  </div>
                  {compactPay ? (
                    <div className="flex flex-none flex-row items-center gap-3 border-t border-doubleNickel-gray-200 bg-white px-6 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-doubleNickel-gray-800">
                          {compactPay}
                        </div>
                        {applyBarSummary && (
                          <div className="truncate text-xs text-doubleNickel-gray-500">
                            {applyBarSummary}
                          </div>
                        )}
                      </div>
                      <MantineButton
                        classNames={{
                          root: 'bg-doubleNickel-brand-500 rounded-lg h-11',
                        }}
                        className="flex-none px-6"
                        loading={loadingSubmitApplicant}
                        onClick={handleSubmission}
                      >
                        Apply now
                      </MantineButton>
                    </div>
                  ) : (
                    // No pay to summarize — the button floats over the form on
                    // a white-to-transparent scrim so scrolling text fades out
                    // before it reaches the button edge
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-white from-[82%] to-transparent px-6 pb-5 pt-8">
                      <MantineButton
                        classNames={{
                          root: 'bg-doubleNickel-brand-500 rounded-lg h-11 w-full shadow-lg',
                        }}
                        className="pointer-events-auto"
                        loading={loadingSubmitApplicant}
                        onClick={handleSubmission}
                      >
                        Apply now
                      </MantineButton>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      )}

      {!isSubmitted && showJobInfo && !isDesktop && (
        // Mobile — job info with a sticky Apply bar; the form opens as a
        // bottom sheet so it stays reachable at all times
        <>
          <div
            className={`flex-1 overflow-auto p-3.5 ${
              compactPay ? '' : 'pb-20'
            }`}
          >
            <AllPositionsLink
              companyId={jobListing.company?.companyId}
              boardSearch={boardSearch}
              phone={phoneHint}
              className="mb-2.5"
            />
            <JobInfoPanel jobListing={jobListing} />
          </div>
          {compactPay ? (
            <div className="flex flex-none flex-row items-center gap-3 border-t border-doubleNickel-gray-200 bg-white px-4 py-3 shadow-[0_-6px_18px_rgba(31,39,51,0.08)]">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-doubleNickel-gray-800">
                  {compactPay}
                </div>
                {applyBarSummary && (
                  <div className="truncate text-xs text-doubleNickel-gray-500">
                    {applyBarSummary}
                  </div>
                )}
              </div>
              <MantineButton
                classNames={{
                  root: 'bg-doubleNickel-brand-500 rounded-lg h-11',
                }}
                className="flex-none px-6"
                onClick={openApplySheet}
              >
                Apply now
              </MantineButton>
            </div>
          ) : (
            // No pay to summarize — the button floats over the job info on
            // a page-color scrim so scrolling text fades out before it
            // reaches the button edge
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-doubleNickel-gray-50 from-[75%] to-transparent px-4 pb-4 pt-6">
              <MantineButton
                classNames={{
                  root: 'bg-doubleNickel-brand-500 rounded-lg h-11 w-full shadow-lg',
                }}
                className="pointer-events-auto"
                onClick={openApplySheet}
              >
                Apply now
              </MantineButton>
            </div>
          )}
          <Drawer
            opened={applySheetOpened}
            onClose={closeApplySheet}
            position="bottom"
            size="88%"
            withCloseButton={false}
            padding={0}
            transitionProps={{
              transition: APPLY_SHEET_TRANSITION,
              duration: reduceMotion ? 0 : 300,
              exitDuration: reduceMotion ? 0 : 240,
              // Ease-out with a light ~1.5% overshoot so the sheet settles
              // with a small, quick bounce. On exit the overshoot lands
              // off-screen.
              timingFunction: 'cubic-bezier(0.34, 1.2, 0.64, 1)',
            }}
            styles={{
              content: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                display: 'flex',
                flexDirection: 'column',
                // Overshoot headroom: extend the sheet 24px below the
                // viewport so the bounce never reveals a gap at the bottom.
                height: 'calc(88dvh + 24px)',
                marginBottom: -24,
                paddingBottom: 24,
              },
              body: {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              },
            }}
          >
            <button
              type="button"
              aria-label="Close application form"
              className="flex-none border-b border-doubleNickel-gray-100 px-4 pb-2.5 pt-2"
              onClick={closeApplySheet}
            >
              <div className="mx-auto mb-2.5 h-1 w-9 rounded-full bg-doubleNickel-gray-300" />
              <div className="flex flex-row items-center justify-between">
                <div className="truncate text-sm font-bold text-doubleNickel-gray-800">
                  Apply — {jobListing.title}
                </div>
                <svg
                  className="h-[18px] w-[18px] flex-none text-doubleNickel-gray-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </div>
            </button>
            <div className="flex-1 overflow-auto px-4 py-4">{formCard}</div>
          </Drawer>
        </>
      )}

      {isSubmitted && (
        <FormSubmitScreen
          isOfficeJob={isOfficeJob}
          applicationId={applicationId}
          phone={unformatPhoneNumber(form.values.phone)}
        />
      )}
    </div>
  )
}

export default ShortFormPage
